import * as Sentry from "@sentry/nextjs";
import { getOffers } from "../../../app/api/getOffers";
import { BaseAction } from "../../../core/actions/BaseAction";
import { calculatePrice, convertISO8601ToDuration } from "../../../Lib/Utils/validationUtil";
import { isShuttleProductCode, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IApiResponse, IOffer, IOffersResponse, ITrip, OffersRequest } from "../../../Orchestrator/Types/Index";
import { IShuttleTrip } from "../../../Orchestrator/Types/Interface/IShuttleBus";
import { hhmmInDubai } from "../../../Lib/Utils/shuttleTime";

export interface IGetShuttleE2EOffersParams {
    /** Shuttle stop place ref for the origin leg. */
    fromStopRef: string;
    /** Shuttle stop place ref for the destination leg. */
    toStopRef: string;
    /** ISO date string the retailer expects for the travel day. */
    departureDate: string;
    /** Currently selected booking passengers (externalRef/age/cards/prmNeeds). */
    passengers: { externalRef: string; age?: number; cards?: any[]; prmNeeds?: string[] }[];
}

/** Format an ISO datetime as "HH:mm" in Dubai — NOT the browser's zone, which
    showed a 05:00 Dubai shuttle as 02:00 to a viewer at UTC+1. */
const hhmm = (iso: string): string => hhmmInDubai(iso);

/**
 * Fetches shuttle offers from the API and parses only shuttle-coded offers
 * into IShuttleTrip[].
 *
 * ISOLATED: does not write to any global atoms (trainAtom, offerAtom,
 * filterTrainsAtom) and does not show notifications. Callers:
 * shuttleOffersController.ts (Task 9) and tests.
 */
export default class GetShuttleE2EOffersAction extends BaseAction<IShuttleTrip[], IGetShuttleE2EOffersParams> {
    protected override async run(): Promise<IShuttleTrip[] | null> {
        const { fromStopRef, toStopRef, departureDate, passengers } = this.params!;

        const request: OffersRequest = {
            tripSearchCriteria: {
                departureTime: departureDate,
                origin: { stopPlaceRef: fromStopRef, objectType: "StopPlaceRef" },
                destination: { stopPlaceRef: toStopRef, objectType: "StopPlaceRef" },
                // ALWAYS OUT_BOUND, and NOT a caller-supplied value — there is
                // deliberately no `direction` param to pass in.
                //
                // `direction` describes the shuttle's own one-way hop, not which
                // end of the train leg it serves. The retailer stamps the minted
                // offer from it and then rejects an inbound-stamped shuttle at
                // POST /booked-offers with INVALID_OFFER_SELECTION — while
                // POST /offers returns identical trips for BOTH values. That is
                // why "inbound" looked correct all the way to preprod, and why
                // this is a constant here rather than an argument a caller can
                // get wrong again.
                direction: TripTypeEnum.OUT_BOUND,
            },
            offerSearchCriteria: { currency: "AED" },
            anonymousPassengerSpecifications: (passengers || []).map((p) => ({
                externalRef: p.externalRef,
                type: "PERSON",
                age: p.age,
                cards: p.cards || [],
                prmNeeds: p.prmNeeds || [],
                sharePlaceWithGuardian: false,
            })),
        };

        try {
            // No toast, no loader: this runs as a background sweep, one call per
            // shuttle stop, on checkout load — before the user has asked for
            // anything.
            //  - showErrorToast=false: a station with a dead stop would
            //    otherwise pop a generic API toast per stop.
            //  - showLoader=false: startLoaderTracking is a global counter that
            //    only releases at zero, so these would raise the full-screen
            //    loader on their own AND hold a user-initiated request's loader
            //    open until the last sweep landed.
            // Failures still reach Sentry below, and the surfaces raise their
            // own user-facing errors — the modal shows "please try again later"
            // in the time panel, and Confirm toasts when it cannot re-price.
            const response: IApiResponse<IOffersResponse> = await getOffers(request, false, false);
            if (response.status !== "ok" || !response.answer) {
                // Breadcrumb, not an event. This action is now a background
                // sweep on every checkout page view (one call per shuttle stop,
                // both sides, per leg), so a station with a permanently dead
                // stop would raise several Sentry events per page load and
                // drown the project. The breadcrumb still rides along on any
                // real exception captured below or downstream.
                Sentry.addBreadcrumb({
                    category: "shuttle.offers",
                    level: "warning",
                    message: "GetShuttleE2EOffersAction: offers request failed",
                    data: { status: response?.status, code: response?.code },
                });
                return null;
            }

            return this.parseShuttleTrips(response.answer.trips || [], response.answer.offers || []);
        } catch (error) {
            console.error("GetShuttleE2EOffersAction failed:", error);
            Sentry.captureException(error, { tags: { action: "GetShuttleE2EOffersAction" } });
            return null;
        }
    }

    /** Build one IShuttleTrip per trip that has a shuttle-coded offer. */
    parseShuttleTrips(trips: ITrip[], offers: IOffer[]): IShuttleTrip[] {
        const result: IShuttleTrip[] = [];

        for (const trip of trips) {
            const shuttleOffer = offers.find((o) => {
                const product = o.products?.[0];
                if (!product || !isShuttleProductCode(product.code)) return false;
                const cov = o.tripCoverage;
                const matchTrip = cov?.coveredTripId === trip.id;
                const matchLeg = (cov?.coveredLegIds || []).some((id) =>
                    trip.legs.some((l) => l.id === id)
                );
                return matchTrip || matchLeg;
            });

            if (!shuttleOffer) continue;

            // Price: sum admissionOfferParts and apply scale (same pattern as GetOffersForShuttleAction).
            const admissionParts = shuttleOffer.admissionOfferParts || [];
            const firstPart = admissionParts[0];
            const perPaxPrice = firstPart ? calculatePrice(firstPart.price) : 0;
            // One part per passenger group, each carrying that group's own fare
            // (IAdmissionOfferPart.passengerRefs) — so an adult + child offer has
            // two parts at two prices. Multiplying the first part by the head
            // count therefore overstates a mixed party: measured live 2026-08-07,
            // adult 10 + child 5 was displayed as 20 while the receipt billed 15.
            const totalPrice = admissionParts.reduce(
                (sum, part) => sum + calculatePrice(part.price),
                0
            );
            // Same parts, keyed by rider. A part may cover more than one
            // passenger (`passengerRefs` is an array), in which case its price
            // is what those riders cost together — split evenly, since they are
            // grouped precisely because they pay the same fare.
            const priceByRef: Record<string, number> = {};
            for (const part of admissionParts) {
                const refs = part.passengerRefs || [];
                if (refs.length === 0) continue;
                const each = calculatePrice(part.price) / refs.length;
                refs.forEach((ref) => {
                    // ACCUMULATE. A rider may appear in more than one part —
                    // IAdmissionOfferPart carries per-leg products, so nothing in
                    // the schema forbids it — and overwriting would drop every
                    // part but the last, showing LESS than totalPrice bills.
                    // Summing keeps `Σ priceByRef === totalPrice` true for every
                    // shape, including the one-part-per-rider shape measured on
                    // INT where the two forms are identical.
                    priceByRef[ref] = (priceByRef[ref] || 0) + each;
                });
            }

            // Service/train number: vehicleNumbers on the first leg's timedLeg.service.
            const firstLeg = trip.legs?.[0];
            const vehicleNumbers = firstLeg?.timedLeg?.service?.vehicleNumbers;
            const trainNumber = Array.isArray(vehicleNumbers) && vehicleNumbers.length > 0
                ? vehicleNumbers.join(", ")
                : "";

            result.push({
                offerId: shuttleOffer.offerId,
                depTime: hhmm(trip.startTime),
                arrTime: hhmm(trip.endTime),
                depDateTime: trip.startTime,
                arrDateTime: trip.endTime,
                trainNumber,
                perPaxPrice,
                totalPrice,
                priceByRef,
                durationLabel: convertISO8601ToDuration(trip.duration) || "",
            });
        }

        return result;
    }
}
